import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface Option {
  label: string;
  value: string;
}

interface BadgeMultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  renderBadge?: (option: Option) => React.ReactNode;
}

export function BadgeMultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select items...",
  emptyMessage = "No items found.",
  renderBadge,
}: BadgeMultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const handleRemove = (e: React.MouseEvent, itemValue: string) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(value.filter((v) => v !== itemValue));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto min-h-[2.5rem] py-2 hover:bg-transparent"
        >
          <div className="flex flex-wrap gap-1 pe-2">
            {value.length > 0 ? (
              value.map((item) => {
                const option = options.find((opt) => opt.value === item);
                if (!option) return null;

                return renderBadge ? (
                  <Badge key={item} variant="secondary" className="rounded-sm px-1 font-mono">
                    {renderBadge(option)}
                  </Badge>
                ) : (
                  <Badge
                    key={item}
                    variant="secondary"
                    className="rounded-sm px-1 font-mono flex items-center gap-1"
                  >
                    {option.label}
                    <span
                      onClick={(e) => handleRemove(e, item)}
                      className="ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-secondary-foreground/10 p-0.5 transition-colors cursor-pointer"
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground transition-colors" />
                    </span>
                  </Badge>
                );
              })
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ms-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" side="bottom">
        <Command className="w-full">
          <CommandEmpty>{emptyMessage}</CommandEmpty>
          <CommandGroup className="max-h-80 overflow-y-auto">
            {options.map((option) => (
              <CommandItem
                key={option.value}
                onSelect={() => {
                  const newValue = value.includes(option.value)
                    ? value.filter((v) => v !== option.value)
                    : [...value, option.value];
                  onChange(newValue);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value.includes(option.value) ? "opacity-100" : "opacity-0"
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
